//Attribute panel
import React, { Component } from "react";
import { connect } from "react-redux";
import { updateParams } from "../../redux/actions/index";
import styled from "styled-components";
import find from "lodash/find";
import orderBy from "lodash/orderBy";

import classNames from "classnames";
import {
  checkAttrOptionCondition,
  isAttrOptionHasCondition,
  isAttrOptionHasChildren,
  getFlattenAttrs,
  normalizeAttrsParams,
} from "../../helpers/attributes";

import { formatPrice, getTotalPrice } from "../../helpers/price";
import analytics from "../../helpers/analytics";
import InfoNotice from "./InfoNotice";
import VariantBox from "../VariantBox";
import config from "../../config";

import { ReactComponent as ArrowDownIcon } from "../../assets/img/arrow-down-icon.svg";

import "./index.scss";

const StyledInfoNotice = styled(InfoNotice)`
  position: absolute;
  top: 63px;
  right: -39%;
  z-index: 999;
`;

class AttributePanel extends Component {
  state = {
    attribute: false,
    group: false,
    leftPosition: 0,
  };

  constructor(props, context) {
    super(props, context);

    this.attributesBox = React.createRef();
  }

  componentDidMount() {
    this.setAttribute();
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const prev = `${this.props.location.pathname}`;
    const next = `${nextProps.location.pathname}`;

    if (prev !== next) {
      this.setAttribute(nextProps);
      this.attributesBox.current.scrollLeft = 0;
    }
  }

  setAttribute(props) {
    props = props || this.props;

    const attribute = find(getFlattenAttrs(this.props.attributes), {
      slug: props.match.params.attrSlug,
    });

    if (attribute) {
      this.setState(
        {
          attribute,
          group: false,
        },
        () => {
          const options = this.getOptions();
          if (options.length === 1 && isAttrOptionHasChildren(options[0])) {
            this.setGroup(options[0].children);
          }
        }
      );
    } else {
      this.props.history.push({
        pathname: `/${config.attrBase}/${this.props.attributes[0].slug}`,
      });
    }
  }

  onChoose = (option) => {
    if (isAttrOptionHasChildren(option)) {
      if (option.children.length > 1) {
        this.setGroup(option.children);
        if (
          !find(option.children, {
            value: this.props.params[this.state.attribute.slug].value,
          })
        ) {
          const sameFrameColorChild = option.children.find((child) => {
            const [, childFrameColor] = child.imgSlug.split("%");
            const [, prevFrameColor] =
              this.props.params[this.state.attribute.slug].imgSlug.split("%");

            return childFrameColor === prevFrameColor;
          });

          option = sameFrameColorChild
            ? sameFrameColorChild
            : option.children[0];
          this.setValue(option.value);
          this.fireOnChooseOption(option);
        }
      } else {
        option = option.children[0];
        this.setValue(option.value);
        this.fireOnChooseOption(option);
      }
    } else {
      this.setValue(option.value);
      this.fireOnChooseOption(option);
    }
  };

  fireOnChooseOption(option) {
    // Analytics
    const name =
      option.parentName !== undefined
        ? `${option.parentName} ${option.name}`
        : option.name;
    analytics.onChooseOption(name);
  }

  resetGroup = () => {
    this.setState(
      {
        group: false,
      },
      () => {
        this.attributesBox.current.scrollLeft = this.state.leftPosition;
      }
    );
  };

  setGroup = (data) => {
    this.setState(
      {
        group: data,
        leftPosition: this.attributesBox.current.scrollLeft,
      },
      () => {
        this.attributesBox.current.scrollLeft = 0;
      }
    );
  };

  setValue = (value) => {
    this.props.updateParams({
      [this.state.attribute.slug]: value,
    });
  };

  getOptions = () => {
    let options = [];

    this.state.attribute.options.forEach((option) => {
      if (isAttrOptionHasCondition(option)) {
        if (
          checkAttrOptionCondition(
            option.conditions,
            this.props.params,
            this.props.attributes
          )
        ) {
          options = options.concat(option.items);
        }
      } else {
        options.push(option);
      }
    });

    return options;
  };

  isActiveOption = (option) => {
    let isActive = false;

    if (this.props.params[this.state.attribute.slug] !== undefined) {
      const value = this.props.params[this.state.attribute.slug].value;
      if (isAttrOptionHasChildren(option)) {
        isActive = find(option.children, { value });
      } else {
        isActive = option.value === value;
      }
    }

    return isActive;
  };

  getOptionPrice = (option) => {
    let value;

    if (option.children !== undefined) {
      value = orderBy(option.children, ["price"], ["asc"])[0].value;
    } else {
      value = option.value;
    }

    const newParams = normalizeAttrsParams(
      this.props.attributes,
      {
        ...this.props.params,
        [this.state.attribute.slug]: value,
      },
      this.props.touchedAttributes
    );

    const totalPrice = getTotalPrice(newParams);

    return `${formatPrice(totalPrice)} €`;
  };

  getImageUrl = (option, attribute) => {
    if (attribute.slug === "frames") {
      let [_ = "", frameColor = "black"] = option.imgSlug.split("%");
      return `/wp-content/uploads/tables/option-thumbnails/frames/${frameColor}/${option.productionNumber}-${frameColor}.png`;
    } else {
      return option.iconUrl;
    }
  };

  render() {
    if (this.state.attribute === false) {
      return null;
    }

    const { group, attribute } = this.state;

    const options = group === false ? this.getOptions() : group;

    return (
      <React.Fragment>
        <div className={classNames("variations", this.props.className)}>
          {attribute.heading && (
            <div className="variations__heading">{attribute.heading}</div>
          )}

          {attribute.subHeading && (
            <div className="variations__sub-heading">
              {attribute.subHeading}
            </div>
          )}

          <div className="variations__inner" ref={this.attributesBox}>
            {group !== false && this.getOptions().length > 1 && (
              <div
                className={classNames("variations__item", {
                  "variations__item--disabled": this.props.previewLoading,
                })}
                onClick={this.resetGroup}
              >
                {options.length > 1 && (
                  <div className="variations__back">
                    <ArrowDownIcon />
                    {attribute.backText}
                  </div>
                )}
              </div>
            )}

            {options.map((option, key) => (
              <div
                className={classNames("variations__item", {
                  "variations__item--disabled": this.props.previewLoading,
                })}
                onClick={() => this.onChoose(option)}
                key={key}
              >
                {option.value === "massiv-gerade-kante-40mm" && (
                  <StyledInfoNotice />
                )}

                <VariantBox
                  className="variations__variant-box"
                  image={this.getImageUrl(option, attribute)}
                  title={option.name}
                  price={this.getOptionPrice(option)}
                  isActive={this.isActiveOption(option)}
                  info={option.info}
                  badge={option.badge}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="variations-shadow" />
      </React.Fragment>
    );
  }
}

export default connect(
  (state) => ({
    params: state.params,
    attributes: state.attributes,
    totalPrice: state.totalPrice,
    previewLoading: state.previewLoading,
    touchedAttributes: state.touchedAttributes,
  }),
  (dispatch) => ({
    updateParams: (params) => dispatch(updateParams(params)),
  })
)(AttributePanel);
